/**
 * cart-drawer.js
 *
 * Intercepts add to cart, posts to the Ajax cart API, re-renders the drawer
 * section server side and opens it. Replaces the full page reload that
 * previously followed every add.
 *
 * The drawer markup is re-rendered by Shopify's Section Rendering API rather
 * than built here, so money formatting and translations stay in Liquid.
 *
 * Supersedes assets/add-to-cart-feedback.js.
 *
 * Vignovic Agency - Sock Candy Phase 2
 */
(function () {
  'use strict';
 
  var SECTION = 'cart-drawer';
  var NOTE_DEBOUNCE = 600;
 
  var root = null;
  var lastFocus = null;
  var noteTimer = null;
  var notePending = null;
  var noteResolve = null;
 
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
 
  function getRoot() {
    if (!root || !document.body.contains(root)) root = $('[data-cd-root]');
    return root;
  }
 
  function status(msg) {
    var el = getRoot() && $('[data-cd-status]', getRoot());
    if (el) el.textContent = msg;
  }
 
  function busy(on) {
    var r = getRoot();
    if (r) r.classList.toggle('is-busy', !!on);
  }
 
  /* ----------------------------------------------------------- rendering */
 
  function refresh() {
    return fetch(window.location.pathname + '?sections=' + SECTION, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cd-root]');
        var current = getRoot();
        if (!fresh || !current) return;
 
        var wasOpen = current.classList.contains('is-open');
        var hadFocusInNote = document.activeElement &&
          document.activeElement.hasAttribute &&
          document.activeElement.hasAttribute('data-cd-note');
        var caret = hadFocusInNote ? document.activeElement.selectionStart : null;
        var noteValue = hadFocusInNote ? document.activeElement.value : null;
 
        current.innerHTML = fresh.innerHTML;
        current.setAttribute('data-cd-count', fresh.getAttribute('data-cd-count') || '0');
        current.hidden = false;
        if (wasOpen) current.classList.add('is-open');
 
        // Someone typing in the note while a quantity change lands should not
        // lose their place.
        if (hadFocusInNote) {
          var field = $('[data-cd-note]', current);
          if (field) {
            field.hidden = false;
            field.value = noteValue;
            field.focus();
            try { field.setSelectionRange(caret, caret); } catch (e) {}
          }
        }
        syncCount();
      });
  }
 
  function syncCount() {
    var r = getRoot();
    if (!r) return;
    var count = parseInt(r.getAttribute('data-cd-count') || '0', 10);
    // The badge markup ships in the next batch. This no-ops until then.
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count > 0 ? String(count) : '';
      el.setAttribute('data-cart-count', String(count));
      el.hidden = count === 0;
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: count } }));
  }
 
  /* --------------------------------------------------------- open, close */
 
  function open() {
    var r = getRoot();
    if (!r) return;
    lastFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    r.hidden = false;
    // Force a frame so the transition runs from the closed state.
    window.requestAnimationFrame(function () {
      r.classList.add('is-open');
      var panel = $('[data-cd-panel]', r);
      if (panel) panel.focus();
    });
    document.addEventListener('keydown', onKeydown, true);
  }
 
  function close() {
    var r = getRoot();
    if (!r) return;
    document.body.style.overflow = '';
    r.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown, true);
    window.setTimeout(function () {
      if (!r.classList.contains('is-open')) r.hidden = true;
    }, 300);
    if (lastFocus && document.body.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }
 
  function focusables(panel) {
    return $$('a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }
 
  function onKeydown(e) {
    var r = getRoot();
    if (!r || !r.classList.contains('is-open')) return;
 
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
 
    var panel = $('[data-cd-panel]', r);
    if (!panel) return;
    var items = focusables(panel);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
 
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
 
  /* ------------------------------------------------------------ cart ops */
 
  function showError(message) {
    var r = getRoot();
    if (!r) return;
    var foot = $('.cd__foot', r) || $('.cd__body', r);
    if (!foot) return;
    var existing = $('.cd__error', r);
    if (existing) existing.remove();
    var p = document.createElement('p');
    p.className = 'cd__error';
    p.setAttribute('role', 'alert');
    p.textContent = message;
    foot.appendChild(p);
    status(message);
  }
 
  function addToCart(form) {
    busy(true);
    var data = new FormData(form);
    return fetch('/cart/add.js', {
      method: 'POST',
      body: data,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error(json.description || json.message || 'This item could not be added.');
          return json;
        });
      })
      .then(function (json) {
        // The page load to /cart that used to follow an add is gone. Anything
        // that counted adds by observing that page view must bind here instead.
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: json } }));
        return refresh();
      })
      .then(function () {
        open();
        status('Item added to your cart.');
      })
      .catch(function (err) {
        // The drawer may not be rendered yet on a hard failure, so fall back
        // to the cart page rather than stranding the shopper.
        if (getRoot()) { open(); showError(err.message); }
        else { window.location = '/cart'; }
      })
      .then(function () { busy(false); });
  }
 
  function changeLine(line, quantity) {
    busy(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error(json.description || json.message || 'That quantity is not available.');
          return json;
        });
      })
      .then(function () { return refresh(); })
      .then(function () { status('Cart updated.'); })
      .catch(function (err) { showError(err.message); })
      .then(function () { busy(false); });
  }
 
  /* -------------------------------------------------------- gift message */
 
  function postNote(value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ note: value })
    });
  }
 
  function queueNote(value) {
    window.clearTimeout(noteTimer);
    if (!notePending) {
      notePending = new Promise(function (resolve) { noteResolve = resolve; });
    }
    noteTimer = window.setTimeout(function () {
      postNote(value)
        .catch(function () {})
        .then(function () {
          if (noteResolve) noteResolve();
          notePending = null;
          noteResolve = null;
        });
    }, NOTE_DEBOUNCE);
  }
 
  function flushNote() {
    var r = getRoot();
    var field = r && $('[data-cd-note]', r);
    var toggle = r && $('[data-cd-gift-toggle]', r);
    if (!field) return Promise.resolve();
 
    window.clearTimeout(noteTimer);
    var value = (toggle && !toggle.checked) ? '' : field.value;
    return postNote(value).catch(function () {}).then(function () {
      if (noteResolve) { noteResolve(); notePending = null; noteResolve = null; }
    });
  }
 
  /* ------------------------------------------------------------ checkout */
 
  function checkout() {
    busy(true);
    status('Starting checkout.');
    // Wait for any gift message to be saved before leaving the page. A lost
    // gift message on a gift order is worse than a moment's delay.
    flushNote().then(function () { window.location = '/checkout'; });
  }
 
  /* ------------------------------------------------------------ bindings */
 
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || typeof form.matches !== 'function') return;
    if (!form.matches('form[data-product-form]')) return;
    if (!getRoot()) return; // no drawer on the page, let the native post run
    e.preventDefault();
 
    var button = form.querySelector('[data-submit-button]');
    var label = button && button.querySelector('[data-submit-button-text]');
    var original = label ? label.textContent.trim() : null;
    if (label) label.textContent = 'Adding to cart...';
    if (button) { button.setAttribute('aria-busy', 'true'); button.style.opacity = '0.65'; }
 
    addToCart(form).then(function () {
      if (label && original) label.textContent = original;
      if (button) { button.removeAttribute('aria-busy'); button.style.opacity = ''; }
    });
  }, true);
 
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
 
    if (t.closest('[data-cd-close]')) { e.preventDefault(); close(); return; }
    if (t.closest('[data-cd-checkout]')) { e.preventDefault(); checkout(); return; }
    if (t.closest('[data-cd-open]')) { e.preventDefault(); open(); return; }
 
    var qty = t.closest('[data-cd-qty]');
    if (qty) {
      e.preventDefault();
      changeLine(parseInt(qty.getAttribute('data-cd-qty-line'), 10),
                 parseInt(qty.getAttribute('data-cd-qty'), 10));
    }
  });
 
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.hasAttribute || !t.hasAttribute('data-cd-gift-toggle')) return;
    var r = getRoot();
    var field = r && $('[data-cd-note]', r);
    if (!field) return;
 
    if (t.checked) {
      field.hidden = false;
      field.focus();
      if (field.value.trim()) queueNote(field.value);
    } else {
      // Keep what they typed in the box, but clear it from the cart so an
      // abandoned message never reaches the order.
      field.hidden = true;
      queueNote('');
    }
  });
 
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.hasAttribute || !t.hasAttribute('data-cd-note')) return;
    var toggle = getRoot() && $('[data-cd-gift-toggle]', getRoot());
    if (toggle && !toggle.checked) return;
    queueNote(t.value);
  });
 
  // iOS restores the page from bfcache with the DOM exactly as it was, which
  // means the is-busy class set before checkout comes back with it and leaves
  // the drawer greyed out and unclickable.
  window.addEventListener('pageshow', function () {
    var r = getRoot();
    if (r) r.classList.remove('is-busy');
    document.body.style.overflow = '';
  });
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCount);
  } else {
    syncCount();
  }
})();
